import { dashboardThemeColors, type DashboardColors } from '@jema-manager/shared';
/** Public deployment identity. No secrets belong in this configuration. */
export interface BrandingConfig {
  name?: string;
  logoUrl?: string;
  iconUrl?: string;
  supportEmail?: string;
  websiteUrl?: string;
  onboardingUrl?: string;
  termsUrl?: string;
  privacyUrl?: string;
  colors?: {
    primary?: string;
    primaryForeground?: string;
    darkPrimary?: string;
    darkPrimaryForeground?: string;
    accent?: string;
    accentForeground?: string;
    darkAccent?: string;
    darkAccentForeground?: string;
  };
}

declare global {
  interface Window {
    __APP_BRANDING__?: BrandingConfig;
  }
}

/** Reject executable URLs; root-relative assets and HTTP(S) destinations are supported. */
export function publicUrl(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const trimmed = value.trim();
  try {
    const url = new URL(trimmed, window.location.origin);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
}

const runtime = window.__APP_BRANDING__ ?? {};
const colors: NonNullable<BrandingConfig['colors']> = {};
for (const key of [
  'primary',
  'primaryForeground',
  'darkPrimary',
  'darkPrimaryForeground',
  'accent',
  'accentForeground',
  'darkAccent',
  'darkAccentForeground',
] as const) {
  const value = runtime.colors?.[key];
  // Values are also embedded in exported document CSS, so reject markup and declarations.
  if (
    typeof value === 'string' &&
    !/[<>;{}]/.test(value) &&
    (globalThis.CSS?.supports?.('color', value) ?? /^#[\da-f]{3,8}$/i.test(value))
  ) {
    colors[key] = value;
  }
}
const deploymentColors = { ...colors };
const email = (runtime.supportEmail ?? import.meta.env.VITE_SUPPORT_EMAIL ?? '').trim();
export const branding = {
  name: (runtime.name ?? import.meta.env.VITE_APP_NAME ?? '').trim(),
  logoUrl: publicUrl(runtime.logoUrl ?? import.meta.env.VITE_BRAND_LOGO_URL),
  iconUrl: publicUrl(runtime.iconUrl ?? import.meta.env.VITE_BRAND_ICON_URL) ?? '/app-icon.svg',
  supportEmail: /^[^\s@?&#]+@[^\s@?&#]+\.[^\s@?&#]+$/.test(email) ? email : undefined,
  websiteUrl: publicUrl(runtime.websiteUrl ?? import.meta.env.VITE_WEBSITE_URL),
  onboardingUrl: publicUrl(runtime.onboardingUrl ?? import.meta.env.VITE_PATIENT_ONBOARDING_URL),
  termsUrl: publicUrl(runtime.termsUrl ?? import.meta.env.VITE_TERMS_URL),
  privacyUrl: publicUrl(runtime.privacyUrl ?? import.meta.env.VITE_PRIVACY_URL),
  colors,
};

export function supportHref(subject?: string, body?: string): string | undefined {
  if (!branding.supportEmail) return undefined;
  const query = new URLSearchParams();
  if (subject) query.set('subject', subject);
  if (body) query.set('body', body);
  return `mailto:${branding.supportEmail}${query.size ? `?${query}` : ''}`;
}

export function applyBranding() {
  if (branding.name) document.title = branding.name;
  const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (icon) {
    icon.href = branding.iconUrl;
    icon.removeAttribute('type');
  }
  for (const key of [
    'primary',
    'primaryForeground',
    'darkPrimary',
    'darkPrimaryForeground',
    'accent',
    'accentForeground',
    'darkAccent',
    'darkAccentForeground',
  ]) {
    const token = key.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
    document.documentElement.style.removeProperty(`--identity-${token}`);
  }
  for (const [key, value] of Object.entries(branding.colors)) {
    if (typeof value === 'string') {
      const token = key.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
      document.documentElement.style.setProperty(`--identity-${token}`, value);
    }
  }
}

const editableColor = (value: string | undefined, fallback: string) =>
  value && /^#[\da-f]{6}$/i.test(value) ? value : fallback;
export const deploymentPalette: Required<DashboardColors> = {
  primary: editableColor(deploymentColors.primary, '#262626'),
  accent: editableColor(deploymentColors.accent, '#ededed'),
  darkPrimary: editableColor(deploymentColors.darkPrimary, '#d4d4d4'),
  darkAccent: editableColor(deploymentColors.darkAccent, '#303030'),
};

export function applyDashboardColors(saved: DashboardColors) {
  branding.colors = { ...deploymentColors, ...dashboardThemeColors(saved) };
  applyBranding();
}
